/**
 * Vendor Detail - Vendor Profile and Products View
 *
 * Displays detailed vendor information:
 * - Vendor profile with contact info
 * - Products list with certification status
 * - Assessment history
 * - Active certifications
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-529
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

type VendorStatus = 'active' | 'inactive' | 'archived';
type ProductStatus = 'active' | 'discontinued' | 'archived';
type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze' | null;
type AssessmentStatus = 'draft' | 'in_progress' | 'pending_review' | 'approved' | 'rejected';

type ProductCategory =
  | 'Drug Discovery'
  | 'Clinical Trial'
  | 'Real World Evidence'
  | 'Pharmacovigilance'
  | 'Manufacturing'
  | 'Regulatory'
  | 'Genomics'
  | 'Biomarker'
  | 'Medical Imaging'
  | 'NLP/Text Mining'
  | 'Other';

interface AIComponent {
  type: string;
  name: string;
  version?: string;
  description?: string;
}

interface Vendor {
  vendor_id: string;
  name: string;
  legal_entity?: string;
  headquarters_country?: string;
  headquarters_city?: string;
  website?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  logo_url?: string;
  description?: string;
  status: VendorStatus;
  created_at: string;
  updated_at: string;
}

interface Product {
  product_id: string;
  vendor_id: string;
  name: string;
  version?: string;
  category?: ProductCategory;
  description?: string;
  ai_components: AIComponent[];
  documentation_url?: string;
  status: ProductStatus;
  created_at: string;
  updated_at: string;
  // Computed
  latest_certification?: CertificationTier;
  certification_expires?: string;
  assessments_count?: number;
  // Findings - from latest assessment
  unresolved_findings_count?: number;
}

interface Assessment {
  assessment_id: string;
  product_id: string;
  product_name: string;
  status: AssessmentStatus;
  overall_score?: number;
  station_scores?: Record<string, number>;
  created_at: string;
  completed_at?: string;
}

interface Certification {
  certification_id: string;
  product_id: string;
  product_name: string;
  tier: CertificationTier;
  issued_at: string;
  expires_at: string;
  gxp_approved: boolean;
}

interface VendorContact {
  contact_id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  is_primary: boolean;
}

interface StationScores {
  data_quality?: number;
  ml_readiness?: number;
  ontology?: number;
  fair?: number;
}

type TabType = 'products' | 'certifications' | 'history' | 'contacts';

// ============================================================================
// CONFIGURATION
// ============================================================================

const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  platinum: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
  gold: { bg: '#fefce8', text: '#a16207', border: '#fde047' },
  silver: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  bronze: { bg: '#fdf4e7', text: '#92400e', border: '#fcd34d' },
};

const STATION_LABELS: Record<string, string> = {
  data_quality: 'Data Quality',
  ml_readiness: 'ML Readiness',
  ontology: 'Ontology',
  fair: 'FAIR',
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const CertificationBadge: React.FC<{ tier: CertificationTier; size?: 'sm' | 'md' | 'lg' }> = ({
  tier,
  size = 'md'
}) => {
  if (!tier) {
    return <span className={`cert-badge cert-none cert-${size}`}>Not Certified</span>;
  }

  return (
    <span className={`cert-badge cert-${tier} cert-${size}`}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
};

const _StatusBadge: React.FC<{ status: AssessmentStatus }> = ({ status }) => {
  const statusLabels: Record<AssessmentStatus, string> = {
    draft: 'Draft',
    in_progress: 'In Progress',
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
  };

  return (
    <span className={`status-badge status-${status.replace('_', '-')}`}>
      {statusLabels[status]}
    </span>
  );
};

// ============================================================================
// STATION SCORE BAR (Phase 7.2)
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#ca8a04';
  return '#dc2626';
}

const StationScoreBar: React.FC<{
  stationKey: string;
  score: number;
  showLabel?: boolean;
}> = ({ stationKey, score, showLabel = true }) => {
  const color = getScoreColor(score);
  const label = STATION_LABELS[stationKey] || stationKey;

  return (
    <div style={{ marginBottom: '8px' }}>
      {showLabel && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '4px',
          fontSize: '13px',
        }}>
          <span style={{ color: '#4b5563' }}>{label}</span>
          <span style={{ fontWeight: 600, color }}>{score}%</span>
        </div>
      )}
      <div style={{
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${score}%`,
          backgroundColor: color,
          borderRadius: '4px',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};

const StationScoresPanel: React.FC<{ scores: StationScores }> = ({ scores }) => {
  const stationEntries = Object.entries(scores).filter(([_, val]) => val !== undefined);

  if (stationEntries.length === 0) {
    return <div style={{ color: '#6b7280', fontSize: '13px' }}>No station scores available</div>;
  }

  return (
    <div style={{ marginTop: '12px' }}>
      {stationEntries.map(([key, value]) => (
        <StationScoreBar key={key} stationKey={key} score={value as number} />
      ))}
    </div>
  );
};

// ============================================================================
// REVOKE CERTIFICATION MODAL (Phase 7.4)
// ============================================================================

const RevokeCertificationModal: React.FC<{
  isOpen: boolean;
  certification: Certification | null;
  onClose: () => void;
  onConfirm: (certificationId: string, reason: string) => Promise<void>;
}> = ({ isOpen, certification, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !certification) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Revocation reason is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onConfirm(certification.certification_id, reason);
      setReason('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke certification');
    } finally {
      setSubmitting(false);
    }
  };

  const tierStyle = certification.tier ? TIER_COLORS[certification.tier] : { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Revoke Certification</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <p style={{ color: '#dc2626', fontWeight: 600, margin: 0 }}>
              Warning: This action cannot be undone.
            </p>
            <p style={{ color: '#7f1d1d', margin: '8px 0 0 0', fontSize: '14px' }}>
              Revoking this certification will immediately invalidate it and notify the vendor.
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
            }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '6px',
                backgroundColor: tierStyle.bg,
                color: tierStyle.text,
                border: `1px solid ${tierStyle.border}`,
                fontWeight: 600,
                fontSize: '13px',
              }}>
                {certification.tier ? certification.tier.charAt(0).toUpperCase() + certification.tier.slice(1) : 'N/A'}
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>{certification.product_name}</div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Issued: {new Date(certification.issued_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="revoke-reason">Reason for Revocation *</label>
            <textarea
              id="revoke-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide detailed reason for revoking this certification..."
              rows={4}
              style={{ width: '100%', resize: 'vertical' }}
              required
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            style={{
              backgroundColor: '#dc2626',
              color: '#fff',
              border: 'none',
            }}
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
          >
            {submitting ? 'Revoking...' : 'Revoke Certification'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONTACT CARD (Phase 7.1 - Contacts Tab)
// ============================================================================

const ContactCard: React.FC<{ contact: VendorContact }> = ({ contact }) => {
  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: contact.is_primary ? '#f0fdf4' : '#fff',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>{contact.name}</h4>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>{contact.role}</span>
        </div>
        {contact.is_primary && (
          <span style={{
            backgroundColor: '#16a34a',
            color: '#fff',
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '4px',
          }}>
            Primary
          </span>
        )}
      </div>
      <div style={{ marginTop: '12px', fontSize: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ color: '#6b7280' }}>Email:</span>
          <a href={`mailto:${contact.email}`} style={{ color: '#2563eb' }}>{contact.email}</a>
        </div>
        {contact.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#6b7280' }}>Phone:</span>
            <span>{contact.phone}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// HISTORY ITEM (Phase 7.1 - History Tab)
// ============================================================================

interface HistoryItem {
  event_id: string;
  action_type: string;
  entity_name: string;
  details?: string;
  actor_name: string;
  created_at: string;
}

const HistoryItemCard: React.FC<{ item: HistoryItem }> = ({ item }) => {
  const actionColors: Record<string, string> = {
    assessment_created: '#2563eb',
    assessment_approved: '#16a34a',
    assessment_rejected: '#dc2626',
    certification_issued: '#16a34a',
    certification_revoked: '#dc2626',
    product_created: '#2563eb',
    product_updated: '#6b7280',
  };

  const color = actionColors[item.action_type] || '#6b7280';
  const label = item.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      padding: '12px',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
        marginTop: '6px',
        flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, fontSize: '14px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.entity_name}</div>
        {item.details && (
          <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '4px' }}>{item.details}</div>
        )}
        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
          {item.actor_name} • {new Date(item.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ENHANCED PRODUCT CARD (Phase 7.5)
// ============================================================================

interface ProductCardProps {
  product: Product;
  stationScores?: StationScores;
  overallScore?: number;
  onView: (id: string) => void;
  onAssess: (id: string) => void;
  onViewReport?: (id: string) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  stationScores,
  overallScore,
  onView,
  onAssess,
  onViewReport,
}) => {
  const tierStyle = product.latest_certification
    ? TIER_COLORS[product.latest_certification]
    : null;

  return (
    <div className="product-card" onClick={() => onView(product.product_id)}>
      <div className="product-card-header">
        <div className="product-info">
          <h4 className="product-name">{product.name}</h4>
          {product.version && (
            <span className="product-version">v{product.version}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {overallScore !== undefined && (
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              color: getScoreColor(overallScore),
            }}>
              {overallScore}%
            </span>
          )}
          {tierStyle ? (
            <span style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '6px',
              backgroundColor: tierStyle.bg,
              color: tierStyle.text,
              border: `1px solid ${tierStyle.border}`,
              fontWeight: 600,
              fontSize: '12px',
            }}>
              {product.latest_certification!.charAt(0).toUpperCase() + product.latest_certification!.slice(1)}
            </span>
          ) : (
            <span style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '6px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              fontSize: '12px',
            }}>
              Not Certified
            </span>
          )}
        </div>
      </div>

      {product.category && (
        <span className="product-category">{product.category}</span>
      )}

      {product.description && (
        <p className="product-description">{product.description}</p>
      )}

      {/* Station Scores Section (Phase 7.2) */}
      {stationScores && Object.keys(stationScores).length > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
            Station Scores
          </div>
          <StationScoresPanel scores={stationScores} />
        </div>
      )}

      {product.ai_components && product.ai_components.length > 0 && (
        <div className="product-ai-components">
          <span className="label">AI Components:</span>
          <div className="component-tags">
            {product.ai_components.slice(0, 3).map((comp, idx) => (
              <span key={idx} className="component-tag">{comp.name}</span>
            ))}
            {product.ai_components.length > 3 && (
              <span className="component-tag more">+{product.ai_components.length - 3}</span>
            )}
          </div>
        </div>
      )}

      {/* Findings Warning (Phase 7) */}
      {product.unresolved_findings_count !== undefined && product.unresolved_findings_count > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#92400e',
        }}>
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span>
            <strong>{product.unresolved_findings_count}</strong> finding{product.unresolved_findings_count !== 1 ? 's' : ''} require remediation
          </span>
        </div>
      )}

      <div className="product-card-actions" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-secondary btn-sm" onClick={() => onView(product.product_id)}>
          View Details
        </button>
        {onViewReport && product.latest_certification && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onViewReport(product.product_id)}
            title="View Full Report"
          >
            Report
          </button>
        )}
        <button className="btn btn-primary btn-sm" onClick={() => onAssess(product.product_id)}>
          Assess
        </button>
      </div>
    </div>
  );
};

const AddProductModal: React.FC<{
  isOpen: boolean;
  vendorId: string;
  onClose: () => void;
  onSubmit: (product: Partial<Product>) => Promise<void>;
}> = ({ isOpen, vendorId, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    category: '' as ProductCategory | '',
    description: '',
    documentation_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories: ProductCategory[] = [
    'Drug Discovery',
    'Clinical Trial',
    'Real World Evidence',
    'Pharmacovigilance',
    'Manufacturing',
    'Regulatory',
    'Genomics',
    'Biomarker',
    'Medical Imaging',
    'NLP/Text Mining',
    'Other',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        vendor_id: vendorId,
        name: formData.name,
        version: formData.version || undefined,
        category: formData.category || undefined,
        description: formData.description || undefined,
        documentation_url: formData.documentation_url || undefined,
        ai_components: [],
      });
      setFormData({
        name: '',
        version: '',
        category: '',
        description: '',
        documentation_url: '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Product</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="product-name">Product Name *</label>
              <input
                id="product-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., DrugScan AI"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="product-version">Version</label>
                <input
                  id="product-version"
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="e.g., 2.1.0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="product-category">Category</label>
                <select
                  id="product-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as ProductCategory })}
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="product-description">Description</label>
              <textarea
                id="product-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the product and its AI/ML capabilities..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="product-docs">Documentation URL</label>
              <input
                id="product-docs"
                type="url"
                value={formData.documentation_url}
                onChange={(e) => setFormData({ ...formData, documentation_url: e.target.value })}
                placeholder="https://docs.example.com/product"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// ASSESSMENT PRODUCT PICKER MODAL
// ============================================================================

const AssessmentProductPickerModal: React.FC<{
  isOpen: boolean;
  products: Product[];
  vendorId: string;
  onClose: () => void;
  onSelect: (productId: string) => void;
  onAddProduct: () => void;
}> = ({ isOpen, products, vendorId: _vendorId, onClose, onSelect, onAddProduct }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Product to Assess</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {products.length === 0 ? (
            <div className="empty-products-state">
              <p>No products available for this vendor.</p>
              <p>You need to add a product before starting an assessment.</p>
            </div>
          ) : (
            <div className="product-picker-list">
              {products.map((product) => (
                <div
                  key={product.product_id}
                  className="product-picker-item"
                  onClick={() => onSelect(product.product_id)}
                >
                  <div className="product-picker-info">
                    <h4>{product.name}</h4>
                    {product.version && <span className="version">v{product.version}</span>}
                    {product.category && (
                      <span className="category">{product.category}</span>
                    )}
                  </div>
                  <div className="product-picker-status">
                    <CertificationBadge tier={product.latest_certification || null} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onAddProduct}>
            + Add New Product
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PRODUCT DETAIL MODAL
// ============================================================================

const ProductDetailModal: React.FC<{
  isOpen: boolean;
  product: Product | null;
  vendorId: string;
  onClose: () => void;
  onAssess: (productId: string) => void;
}> = ({ isOpen, product, vendorId: _vendorId, onClose, onAssess }) => {
  if (!isOpen || !product) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="product-detail-content">
            {product.version && (
              <div className="detail-row">
                <span className="detail-label">Version:</span>
                <span className="detail-value">{product.version}</span>
              </div>
            )}

            {product.category && (
              <div className="detail-row">
                <span className="detail-label">Category:</span>
                <span className="detail-value">{product.category}</span>
              </div>
            )}

            {product.description && (
              <div className="detail-row">
                <span className="detail-label">Description:</span>
                <p className="detail-value description">{product.description}</p>
              </div>
            )}

            {product.documentation_url && (
              <div className="detail-row">
                <span className="detail-label">Documentation:</span>
                <a
                  href={product.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-link"
                >
                  {product.documentation_url}
                </a>
              </div>
            )}

            {product.ai_components && product.ai_components.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">AI Components:</span>
                <div className="ai-components-list">
                  {product.ai_components.map((comp, idx) => (
                    <div key={idx} className="ai-component-item">
                      <strong>{comp.name}</strong>
                      {comp.type && <span className="comp-type">({comp.type})</span>}
                      {comp.description && <p>{comp.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`status-badge status-${product.status}`}>
                {product.status}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              onAssess(product.product_id);
              onClose();
            }}
          >
            Start Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface VendorDetailProps {
  startAssessment?: boolean;
}

export const VendorDetail: React.FC<VendorDetailProps> = ({ startAssessment = false }) => {
  const { vendorId, productId } = useParams<{ vendorId: string; productId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [contacts, setContacts] = useState<VendorContact[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [productScores, setProductScores] = useState<Record<string, { overall?: number; stations?: StationScores }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab from URL or default to 'products' (Phase 7.1)
  const activeTab = (searchParams.get('tab') as TabType) || 'products';
  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab });
  };

  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(productId || null);
  const [showAssessmentProductPicker, setShowAssessmentProductPicker] = useState(startAssessment);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Vendor_Detail_Guide.md' })
      });
    } catch (err) {
      console.error('[VendorDetail] Error opening guide:', err);
    }
  };

  const fetchVendorData = useCallback(async () => {
    if (!vendorId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch vendor details
      const vendorRes = await fetch(`/api/vendors/${vendorId}`);
      if (!vendorRes.ok) {
        if (vendorRes.status === 404) {
          throw new Error('Vendor not found');
        }
        throw new Error('Failed to fetch vendor');
      }
      const vendorData = await vendorRes.json();
      // API returns { vendor: {...}, products: [...] }
      setVendor(vendorData.vendor);
      setProducts(vendorData.products || []);

      // Fetch assessments (if endpoint exists)
      try {
        const assessmentsRes = await fetch(`/api/vendors/${vendorId}/assessments`);
        if (assessmentsRes.ok) {
          const assessmentsData = await assessmentsRes.json();
          setAssessments(assessmentsData.assessments || []);
        }
      } catch {
        // Endpoint may not exist yet
        setAssessments([]);
      }

      // Fetch certifications (if endpoint exists)
      try {
        const certsRes = await fetch(`/api/vendors/${vendorId}/certifications`);
        if (certsRes.ok) {
          const certsData = await certsRes.json();
          setCertifications(certsData.certifications || []);
        }
      } catch {
        // Endpoint may not exist yet
        setCertifications([]);
      }

      // Fetch contacts (if endpoint exists)
      try {
        const contactsRes = await fetch(`/api/vendors/${vendorId}/contacts`);
        if (contactsRes.ok) {
          const contactsData = await contactsRes.json();
          setContacts(contactsData.contacts || []);
        }
      } catch {
        // Endpoint may not exist yet - create default contact from vendor info
        if (vendorData.vendor?.contact_name || vendorData.vendor?.contact_email) {
          setContacts([{
            contact_id: 'primary',
            name: vendorData.vendor.contact_name || 'Primary Contact',
            email: vendorData.vendor.contact_email || '',
            phone: vendorData.vendor.contact_phone,
            role: 'Primary Contact',
            is_primary: true,
          }]);
        } else {
          setContacts([]);
        }
      }

      // Fetch history/audit trail (if endpoint exists)
      try {
        const historyRes = await fetch(`/api/vendors/${vendorId}/history?limit=20`);
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setHistory(historyData.events || []);
        }
      } catch {
        // Endpoint may not exist yet
        setHistory([]);
      }

      // Fetch product scores (if endpoint exists)
      try {
        const scoresRes = await fetch(`/api/vendors/${vendorId}/product-scores`);
        if (scoresRes.ok) {
          const scoresData = await scoresRes.json();
          setProductScores(scoresData.scores || {});
        }
      } catch {
        // Endpoint may not exist yet
        setProductScores({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchVendorData();
  }, [fetchVendorData]);

  const handleAddProduct = async (productData: Partial<Product>) => {
    const response = await fetch(`/api/vendors/${vendorId}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create product');
    }

    await fetchVendorData();
  };

  const handleViewProduct = (prodId: string) => {
    setSelectedProductId(prodId);
  };

  const handleCloseProductDetail = () => {
    setSelectedProductId(null);
    // If we navigated here with productId in URL, go back to vendor page
    if (productId) {
      navigate(`/vendors/${vendorId}`);
    }
  };

  const handleAssessProduct = (productId: string) => {
    navigate(`/vendors/${vendorId}/products/${productId}/assess`);
  };

  const _handleViewAssessment = (assessmentId: string) => {
    navigate(`/vendors/${vendorId}/assessments/${assessmentId}`);
  };

  // Phase 7.3: View Full Report with PDF download
  const handleViewReport = async (productIdParam: string) => {
    // Navigate to report view, or download PDF directly
    navigate(`/vendors/${vendorId}/products/${productIdParam}/report`);
  };

  const handleDownloadReport = async (productIdParam: string) => {
    try {
      const response = await fetch(
        `/api/vendors/${vendorId}/products/${productIdParam}/report/pdf`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const product = products.find(p => p.product_id === productIdParam);
      a.download = `certification-report-${product?.name || productIdParam}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report');
    }
  };

  // Phase 7.4: Revoke Certification
  const handleRevokeCertification = async (certificationId: string, reason: string) => {
    const response = await fetch(`/api/certifications/${certificationId}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to revoke certification');
    }

    // Refresh data
    await fetchVendorData();
  };

  const openRevokeCertificationModal = (cert: Certification) => {
    setSelectedCertification(cert);
    setShowRevokeModal(true);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="vendor-detail">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading vendor details...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="vendor-detail">
        <div className="error-state">
          <p>{error || 'Vendor not found'}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/vendors')}>
            Back to Vendors
          </button>
        </div>
      </div>
    );
  }

  const location = [vendor.headquarters_city, vendor.headquarters_country]
    .filter(Boolean)
    .join(', ');

  const activeCerts = certifications.filter(
    (c) => new Date(c.expires_at) > new Date()
  );

  return (
    <div className="vendor-detail">
      {/* Header */}
      <div className="detail-header">
        <button className="btn btn-ghost back-btn" onClick={() => navigate('/vendors')}>
          ← Back to Vendors
        </button>
      </div>

      {/* Vendor Profile */}
      <div className="vendor-profile">
        <div className="profile-header">
          <div className="vendor-logo large">
            {vendor.logo_url ? (
              <img src={vendor.logo_url} alt={vendor.name} />
            ) : (
              <span className="vendor-logo-placeholder">
                {vendor.name.substring(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="profile-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1>{vendor.name}</h1>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenGuide();
                }}
                className="help-link"
                title="Open Vendor Detail User Guide"
                style={{
                  fontSize: '1.25rem',
                  textDecoration: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                  e.currentTarget.style.borderColor = '#bfdbfe';
                }}
              >
                ?
              </a>
            </div>
            {vendor.legal_entity && (
              <span className="vendor-legal">{vendor.legal_entity}</span>
            )}
            {location && (
              <span className="vendor-location">📍 {location}</span>
            )}
          </div>
          <div className="profile-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/vendors/reports/audit-trail?vendor=${vendorId}`)}
              title="View audit trail for this vendor"
            >
              View Audit Trail
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddProductModal(true)}
            >
              + Add Product
            </button>
          </div>
        </div>

        {vendor.description && (
          <p className="vendor-description-full">{vendor.description}</p>
        )}

        <div className="vendor-contact-info">
          {vendor.contact_name && (
            <div className="contact-item">
              <span className="label">Contact:</span>
              <span className="value">{vendor.contact_name}</span>
            </div>
          )}
          {vendor.contact_email && (
            <div className="contact-item">
              <span className="label">Email:</span>
              <a href={`mailto:${vendor.contact_email}`}>{vendor.contact_email}</a>
            </div>
          )}
          {vendor.contact_phone && (
            <div className="contact-item">
              <span className="label">Phone:</span>
              <span className="value">{vendor.contact_phone}</span>
            </div>
          )}
          {vendor.website && (
            <div className="contact-item">
              <span className="label">Website:</span>
              <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                {vendor.website}
              </a>
            </div>
          )}
        </div>

        <div className="vendor-meta">
          <span>Added: {formatDate(vendor.created_at)}</span>
          <span>Status: <span className={`status-${vendor.status}`}>{vendor.status}</span></span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-value">{products.length}</div>
          <div className="summary-label">Products</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{assessments.length}</div>
          <div className="summary-label">Assessments</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{activeCerts.length}</div>
          <div className="summary-label">Active Certifications</div>
        </div>
      </div>

      {/* Tabs - Phase 7.1: Updated with new tabs and URL sync */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products ({products.length})
          </button>
          <button
            className={`tab ${activeTab === 'certifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('certifications')}
          >
            Certifications ({activeCerts.length})
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History ({history.length})
          </button>
          <button
            className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts ({contacts.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Products Tab - Phase 7.2 & 7.5: Enhanced with station scores */}
          {activeTab === 'products' && (
            <div className="products-tab">
              {products.length === 0 ? (
                <div className="empty-tab-state">
                  <p>No products added yet.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddProductModal(true)}
                  >
                    Add First Product
                  </button>
                </div>
              ) : (
                <div className="product-grid">
                  {products.map((product) => (
                    <ProductCard
                      key={product.product_id}
                      product={product}
                      stationScores={productScores[product.product_id]?.stations}
                      overallScore={productScores[product.product_id]?.overall}
                      onView={handleViewProduct}
                      onAssess={handleAssessProduct}
                      onViewReport={handleViewReport}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Certifications Tab - Phase 7.3 & 7.4: Enhanced with View Report and Revoke buttons */}
          {activeTab === 'certifications' && (
            <div className="certifications-tab">
              {activeCerts.length === 0 ? (
                <div className="empty-tab-state">
                  <p>No active certifications. Complete an assessment to earn certification.</p>
                </div>
              ) : (
                <div className="certifications-list">
                  {activeCerts.map((cert) => {
                    const tierStyle = cert.tier ? TIER_COLORS[cert.tier] : { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' };
                    return (
                      <div key={cert.certification_id} className="certification-card" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              backgroundColor: tierStyle.bg,
                              color: tierStyle.text,
                              border: `1px solid ${tierStyle.border}`,
                              fontWeight: 700,
                              fontSize: '14px',
                            }}>
                              {cert.tier ? cert.tier.charAt(0).toUpperCase() + cert.tier.slice(1) : 'N/A'}
                            </span>
                            {cert.gxp_approved && (
                              <span style={{
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                fontSize: '11px',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontWeight: 600,
                              }}>
                                GxP Approved
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {/* Phase 7.3: View Full Report button */}
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleViewReport(cert.product_id)}
                              title="View Full Report"
                            >
                              View Report
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDownloadReport(cert.product_id)}
                              title="Download PDF Report"
                            >
                              PDF
                            </button>
                            {/* Phase 7.4: Revoke Certification button */}
                            <button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                border: '1px solid #fecaca',
                              }}
                              onClick={() => openRevokeCertificationModal(cert)}
                              title="Revoke Certification"
                            >
                              Revoke
                            </button>
                          </div>
                        </div>
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontWeight: 600, fontSize: '15px' }}>{cert.product_name}</div>
                          <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>
                            <span>Issued: {formatDate(cert.issued_at)}</span>
                            <span style={{ margin: '0 12px' }}>•</span>
                            <span>Expires: {formatDate(cert.expires_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* History Tab - Phase 7.1: New tab */}
          {activeTab === 'history' && (
            <div className="history-tab">
              {history.length === 0 ? (
                <div className="empty-tab-state">
                  <p>No activity history available.</p>
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate(`/vendors/reports/audit-trail?entity_type=vendor&entity_id=${vendorId}`)}
                  >
                    View Full Audit Trail
                  </button>
                </div>
              ) : (
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}>
                  {history.map((item) => (
                    <HistoryItemCard key={item.event_id} item={item} />
                  ))}
                  <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                    <button
                      className="btn btn-link"
                      onClick={() => navigate(`/vendors/reports/audit-trail?entity_type=vendor&entity_id=${vendorId}`)}
                    >
                      View Full Audit Trail →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contacts Tab - Phase 7.1: New tab */}
          {activeTab === 'contacts' && (
            <div className="contacts-tab">
              {contacts.length === 0 ? (
                <div className="empty-tab-state">
                  <p>No contacts added yet.</p>
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>
                    Contact information can be added via the vendor profile settings.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '16px',
                }}>
                  {contacts.map((contact) => (
                    <ContactCard key={contact.contact_id} contact={contact} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddProductModal}
        vendorId={vendorId || ''}
        onClose={() => setShowAddProductModal(false)}
        onSubmit={handleAddProduct}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        isOpen={!!selectedProductId}
        product={products.find(p => p.product_id === selectedProductId) || null}
        vendorId={vendorId || ''}
        onClose={handleCloseProductDetail}
        onAssess={handleAssessProduct}
      />

      {/* Assessment Product Picker Modal */}
      <AssessmentProductPickerModal
        isOpen={showAssessmentProductPicker}
        products={products}
        vendorId={vendorId || ''}
        onClose={() => {
          setShowAssessmentProductPicker(false);
          // If we came from the assess route, go back to vendor detail
          if (startAssessment) {
            navigate(`/vendors/${vendorId}`);
          }
        }}
        onSelect={(selectedProdId) => {
          setShowAssessmentProductPicker(false);
          navigate(`/vendors/${vendorId}/products/${selectedProdId}/assess`);
        }}
        onAddProduct={() => {
          setShowAssessmentProductPicker(false);
          setShowAddProductModal(true);
        }}
      />

      {/* Phase 7.4: Revoke Certification Modal */}
      <RevokeCertificationModal
        isOpen={showRevokeModal}
        certification={selectedCertification}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedCertification(null);
        }}
        onConfirm={handleRevokeCertification}
      />
    </div>
  );
};

export default VendorDetail;
